import { AppData, Colors, Lightning } from '@lightningjs/sdk';
import { EpgChannel } from 'types/api/media';
import EpgTimeline from 'components/pages/epg/EpgTimeline';
import EpgRow, {
  MAX_VISIBLE_TABS_WIDTH,
  VISIBLE_TABS_WIDTH,
} from 'components/pages/epg/EpgRow';
import { constants } from 'aliases';
import { getEpgPage } from 'services/cwService';
import { cleanupEpgChannelData } from 'services/cwData';
import { IndexData } from 'types/lightning';
import EpgCell from 'components/pages/epg/EpgCell';
import EpgProgressIndicator from 'components/pages/epg/EpgProgressIndicator';
import { minutesToMilliseconds } from 'support/timeUtils';
import { List } from '@lightningjs/ui';
import { ListWithIndexing } from 'components/common/CollectionWrappersWithIndexing';
import {
  ErrorCode,
  reportError,
} from 'services/analytics/reportingServicePage';
import { HoverableComponent } from 'components/common/HoverableComponent';
import { DirectionsType, isVerticalDirection } from 'support/keyEventUtils';

export interface EpgGuideTemplateSpec extends Lightning.Component.TemplateSpec {
  initialChannels: EpgChannel[];
  focusedChannel: EpgChannel | null;
  maxChannels: number;
  addedChannelSlug: string | undefined;

  TimelineWrapper: {
    TimelineClippingWrapper: {
      Timeline: typeof EpgTimeline;
    };
    ProgressClippingWrapper: {
      ProgressIndicatorWrapper: {
        ProgressIndicator: typeof EpgProgressIndicator;
      };
    };
  };
  ChannelsWrapper: {
    Channels: typeof List;
  };
  OverlayGradient: Lightning.textures.RectangleTexture;
}

const EPG_GUIDE_WIDTH = 1758;
const PADDING_LEFT = 41;
const TIMELINE_WIDTH = 1580;
const TIMELINE_OFFSET_X =
  PADDING_LEFT + constants.ui.epgLogoWidth + constants.ui.epgSpacing;
const CHANNEL_LIST_HEIGHT = 500;
const CHANNEL_LIST_OFFSET_Y = 50;
const GRADIENT_W = 118;
const TIMELINE_TAB_WIDTH = constants.ui.epgWidthPerThirtyMin;

const EPG_ROW_HEIGHT = constants.ui.epgCellHeight + constants.ui.epgSpacing;
const NUM_EPG_ROWS = CHANNEL_LIST_HEIGHT / EPG_ROW_HEIGHT;

const PROGRESS_HEIGHT = 50;
const PROGRESS_Y = 8;
const PROGRESS_PADDING_PER_MS = TIMELINE_TAB_WIDTH / minutesToMilliseconds(30);
const PROGRESS_VISUAL_CLIPPING_OFFSET = 50;

// Number of rows before the end before we need to fetch more data
const REQUEST_THRESHOLD = 1;

export default class EpgGuide
  extends HoverableComponent<EpgGuideTemplateSpec>
  implements Lightning.Component.ImplementTemplateSpec<EpgGuideTemplateSpec>
{
  private _channels: EpgChannel[] = [];
  private _maxChannels = 0;
  private _addedChannelSlug: string | undefined = undefined;

  // Focus Position
  private _originCellStartX = 0;
  private _verticalLock = false;
  private _targetScrollPosition = 0;
  private _isEpgLoading = false;

  // Internal properties
  private _currentTime?: Date;
  private _startTime?: Date;

  private _TimelineWrapper = this.getByRef('TimelineWrapper')!;
  private _TimelineClippingWrapper = this._TimelineWrapper.getByRef(
    'TimelineClippingWrapper',
  )!;
  private _Timeline = this._TimelineClippingWrapper.getByRef('Timeline')!;

  private _ProgressClippingWrapper = this._TimelineWrapper.getByRef(
    'ProgressClippingWrapper',
  )!;
  private _ProgressIndicatorWrapper = this._ProgressClippingWrapper.getByRef(
    'ProgressIndicatorWrapper',
  )!;
  private _ProgressIndicator =
    this._ProgressIndicatorWrapper.getByRef('ProgressIndicator')!;
  private _ChannelsWrapper = this.getByRef('ChannelsWrapper')!;
  private _Channels = this._ChannelsWrapper.getByRef('Channels')!;

  override isClickable = false;

  set initialChannels(channels: EpgChannel[]) {
    this._Channels.clear();
    if (!channels.length) return;

    this._channels = channels;
    this.initializeChannels();
  }

  get focusedChannel() {
    const currentRow = this._Channels.currentItem as EpgRow | undefined;
    return currentRow?.epgChannel ?? null;
  }

  set maxChannels(maxChannels: number) {
    this._maxChannels = maxChannels;
    this.initializeChannels();
  }

  set addedChannelSlug(slug: string) {
    this._addedChannelSlug = slug;
  }

  set isEpgLoading(isLoading: boolean) {
    this._isEpgLoading = isLoading;
    this.signal('loading', isLoading);
  }

  static override _template(): Lightning.Component.Template<EpgGuideTemplateSpec> {
    return {
      w: EPG_GUIDE_WIDTH,
      h: (y: number) => 1080 - y,
      TimelineWrapper: {
        x: TIMELINE_OFFSET_X,
        TimelineClippingWrapper: {
          w: TIMELINE_WIDTH,
          h: 34,
          clipping: true,
          Timeline: {
            type: EpgTimeline,
          },
        },
        ProgressClippingWrapper: {
          x: -PROGRESS_VISUAL_CLIPPING_OFFSET,
          w: TIMELINE_WIDTH + PROGRESS_VISUAL_CLIPPING_OFFSET,
          h: PROGRESS_HEIGHT,
          clipping: true,
          ProgressIndicatorWrapper: {
            w: TIMELINE_WIDTH + PROGRESS_VISUAL_CLIPPING_OFFSET,
            h: PROGRESS_HEIGHT,
            ProgressIndicator: {
              type: EpgProgressIndicator,
              y: PROGRESS_Y,
            },
          },
        },
      },
      ChannelsWrapper: {
        x: PADDING_LEFT,
        y: CHANNEL_LIST_OFFSET_Y,
        w: EPG_GUIDE_WIDTH - PADDING_LEFT,
        h: h => h - CHANNEL_LIST_OFFSET_Y,
        clipping: true,
        Channels: {
          h: CHANNEL_LIST_HEIGHT,
          type: ListWithIndexing,
          direction: 'column',
          itemType: EpgRow,
          spacing: constants.ui.epgSpacing,
          requestThreshold: REQUEST_THRESHOLD,
          signals: {
            onRequestItems: '_onRequestChannels',
            onRequestItemsAdded: '_onRequestChannelsAdded',
          },
          forceLoad: true,
        },
      },
      OverlayGradient: {
        x: w => w,
        w: GRADIENT_W,
        h: h => h,
        mountX: 1,
        rect: true,
        colorLeft: Colors('transparent').get(),
        colorRight: Colors('background').get(),
      },
    };
  }

  override _focus() {
    const currentRow = this._Channels.currentItem as EpgRow;
    const cellProgram = currentRow.currentCell?.epgProgram ?? null;

    this.signal('focusedProgramChange', cellProgram);
  }

  override _unfocus() {
    this.signal('guideUnfocused');
  }

  override _getFocused() {
    return this._Channels;
  }

  override _focusChange(
    newFocusedComponent: Lightning.Component | EpgCell,
    prevFocusedComponent: Lightning.Component,
  ) {
    super._focusChange(newFocusedComponent, prevFocusedComponent);

    const cellProgram =
      'epgProgram' in newFocusedComponent
        ? newFocusedComponent.epgProgram
        : null;

    this.signal('focusedProgramChange', cellProgram);
  }

  private async _onRequestChannels(indexData: IndexData) {
    const { dataLength } = indexData;
    const maxChannels = this._maxChannels!;
    if (dataLength >= maxChannels) return false;

    this.isEpgLoading = true;
    try {
      const pageSize = constants.epg.pageSize;
      const pageIndex = Math.floor(dataLength / pageSize) + 1;

      let { channels } = await getEpgPage(pageIndex, pageSize);
      if (channels.length === 0) {
        this.isEpgLoading = false;
        return false;
      }

      if (this._addedChannelSlug) {
        const duplicateChannelIndex = channels.findIndex(
          channel => channel.slug === this._addedChannelSlug,
        );

        if (duplicateChannelIndex !== -1) {
          // Remove duplicate channel from paginated channels
          channels.splice(duplicateChannelIndex, 1);
          this._addedChannelSlug = undefined;
        }
      }

      channels = cleanupEpgChannelData(channels, this._startTime ?? new Date());
      this._channels.push(...channels);

      this.isEpgLoading = false;
      return channels.map(this.buildChannels.bind(this));
    } catch (e) {
      reportError({ error: e, userFacing: false, code: ErrorCode.c3 });

      // Do nothing if getting more channels from the API fails
      this.isEpgLoading = false;
      return false;
    }
  }

  private _onRequestChannelsAdded(obj: any) {
    this.$onProgramIndexChanged(this._targetScrollPosition);
  }

  private initializeChannels() {
    const channels = this._channels;
    if (!channels.length) return;

    const w = this._ChannelsWrapper.w;
    const items = channels.map(this.buildChannels.bind(this));
    const enableRequests = channels.length < this._maxChannels;

    this._Channels.patch({
      w,
      items,
      enableRequests,
    });

    this._Channels.scroll = this.handleChannelsScroll.bind(this);
  }

  private handleChannelsScroll(itemWrapper: unknown, { index }: any) {
    if (this._Channels.isHovered) {
      this._Channels.isHovered = false;
    } else {
      const maxScrollIndex =
        Math.floor(CHANNEL_LIST_HEIGHT / EPG_ROW_HEIGHT) - 1;

      let listY = 0;
      if (index > maxScrollIndex) {
        listY = EPG_ROW_HEIGHT * (maxScrollIndex - index);
      }
      return listY;
    }
  }

  private buildChannels(channel: EpgChannel) {
    const currentTime = this._currentTime;
    const startTime = this._startTime;
    const updateTime = { startTime, currentTime };

    return {
      w: this._Channels.w,
      h: constants.ui.epgCellHeight,
      epgChannel: channel,
      isSelected: false,
      updateTime,
    };
  }

  selectChannel(channel: EpgChannel) {
    this._Channels.items.forEach((row: EpgRow, index: number) => {
      const isSelected = !!row.epgChannel && row.epgChannel.id === channel.id;
      row.isSelected = isSelected;

      if (isSelected) {
        this._Channels.setIndex(index);
      }
    });
  }

  updateTime(currentTime: Date, startTime: Date) {
    this._startTime = startTime;
    this._currentTime = currentTime;

    this._Channels.items.forEach((channelRow: EpgRow) => {
      // We can't call public functions for list items, using setter as workaround
      channelRow.updateTime = { startTime, currentTime };
    });
    this._Timeline.updateTime(currentTime, startTime);
    this.updateProgress(currentTime);
  }

  private updateProgress(currentTime: Date) {
    if (!this._startTime) return;
    const startTime = this._startTime;

    const millisecondsOffset = currentTime.getTime() - startTime.getTime();
    const x =
      millisecondsOffset * PROGRESS_PADDING_PER_MS +
      PROGRESS_VISUAL_CLIPPING_OFFSET;

    this._ProgressIndicator.patch({ x, time: currentTime });
  }

  $onProgramIndexChanged(targetScrollPosition: number) {
    this.handleHorizontalScroll(targetScrollPosition);
  }

  handleHorizontalScroll(scrollTarget: number) {
    this._Channels.items.forEach((row: EpgRow, index: number) => {
      const programs = row.programs;
      if (programs && row.scrollTarget !== scrollTarget) {
        // EpgRow set scrollTarget will handle the scrolling
        row.scrollTarget = scrollTarget;
      }
    });
    this._Timeline.setSmooth('x', scrollTarget, {
      timingFunction: 'ease',
    });
    this._ProgressIndicatorWrapper.setSmooth('x', scrollTarget, {
      timingFunction: 'ease',
    });
    this._ProgressIndicatorWrapper.setSmooth(
      'alpha',
      scrollTarget > -TIMELINE_TAB_WIDTH ? 1 : 0,
    );

    this._targetScrollPosition = scrollTarget;
  }

  override _captureKey(event: KeyboardEvent) {
    if (this._isEpgLoading) return;
    const currentIndex = this._Channels.index;

    if (
      (event.key === 'ArrowUp' && currentIndex > 0) ||
      (event.key === 'ArrowDown' && currentIndex < this._Channels.length - 1)
    ) {
      const nextIndex = currentIndex + (event.key === 'ArrowUp' ? -1 : 1);
      const currentCell =
        this._Channels.items[currentIndex].programs.currentItemWrapper;
      const currentCellStart = currentCell.assignedX;

      if (!this._verticalLock) {
        this._verticalLock = true;
        this._originCellStartX = currentCellStart;
      }
      const nextRow = this._Channels.items[nextIndex];

      nextRow.preventScroll = false;
      let prevAbsDifferenceX = nextRow.programs.wrapper.w || nextRow.w;
      let focusIndex = 0;

      // check every item in the next row to be focused
      for (let i = 0; i < nextRow.programs.wrapper.children.length; i++) {
        const { assignedX } = nextRow.programs.wrapper.children[i];
        // check absolute value of difference in X value between the currently checked index and
        // the origin X point
        const currentAbsDifferenceX = Math.abs(
          this._originCellStartX - assignedX,
        );
        /**
          check if the absolute value of the current index being checked to be focused has a
          smaller DifferenceX than the absolute Value of the previous index's DifferenceX

          if current Index is smaller, record and check the next index (is the index to use if
            it is the last index)
          if prev Index is smaller, break and use the prev Index
        */
        if (currentAbsDifferenceX <= prevAbsDifferenceX) {
          prevAbsDifferenceX = currentAbsDifferenceX;
          focusIndex = i;
        } else {
          break;
        }
      }
      while (nextRow.programs.items[focusIndex].isCompleted) {
        focusIndex++;
      }
      nextRow.setIndex(focusIndex);
    } else {
      this._Channels.items[currentIndex].preventScroll = false;
      this._verticalLock = false;

      const currentProgramIndex = this._Channels.currentItem.programs.index;
      const delta = event.key === 'ArrowLeft' ? -1 : 1;
      const nextIndex = Math.max(currentProgramIndex + delta, 0);
      const programsLength = this._Channels.currentItem.programs.items.length;

      // Check if nextIndex is within bounds before checking isCompleted and
      // ensure no out of bounds error occurs after this check that might cause
      // enter key interaction to be blocked when on the last program of the channel
      if (nextIndex >= programsLength) {
        return false;
      }

      const nextCell = this._Channels.currentItem.programs.items[nextIndex];
      const isCompleted = nextCell?.isCompleted ?? false;
      if (isCompleted) {
        this.signal('handleLeft');
        return true;
      }
    }
    return false;
  }

  override _handleBack() {
    /*
    On the first back press, it should focus on the first playable item of the current channel 
    Pressing back again should focus on the first playable item of the very first channel (row)
    */
    const currentRow = this._Channels.currentItem as EpgRow;
    const firstPlayableIndex = this.getFirstPlayableIndex(currentRow.programs);

    // Already at the first playable program of current row
    if (currentRow.programs.index === firstPlayableIndex) {
      // Already at first playable program of first row. Return false to bubble up
      if (this._Channels.index === 0) return false;

      // Focus on the first row
      this._Channels.setIndex(0);
      // Find the first playable program
      const newRow = this._Channels.currentItem as EpgRow;
      const newPlayableIndex = this.getFirstPlayableIndex(newRow.programs);
      newRow.programs.setIndex(newPlayableIndex);

      return true;
    }

    // Focus on the first playable program of current channel
    currentRow.programs.setIndex(firstPlayableIndex);

    this._verticalLock = false;
    return true;
  }

  private getFirstPlayableIndex(programs: List) {
    return programs.items.findIndex(
      (program: EpgCell) => program && !program.isCompleted,
    );
  }

  override onSwipeAction = (direction: DirectionsType, strength: number) => {
    if (!this.hovered || this.isEpgLoading) return;

    if (isVerticalDirection(direction)) {
      const lastIndexInView =
        Math.abs(Math.floor(this._Channels.wrapper.y / EPG_ROW_HEIGHT)) +
        (NUM_EPG_ROWS - 1);

      const offset =
        Math.ceil(NUM_EPG_ROWS * 0.5 * strength) *
        (direction === 'Down' ? 1 : -1);

      // Calculate movement based off the last index because thats where focus will be except when at the top
      const targetIndex = lastIndexInView + offset;
      this._Channels.setIndex(targetIndex);
    } else {
      let newTarget =
        this._targetScrollPosition +
        VISIBLE_TABS_WIDTH * 0.5 * strength * (direction === 'Left' ? 1 : -1);
      if (direction === 'Left') {
        newTarget = Math.min(newTarget, 0);
      } else if (direction === 'Right') {
        newTarget = Math.max(newTarget, -MAX_VISIBLE_TABS_WIDTH);
      }

      this.handleHorizontalScroll(newTarget);
    }
  };
}
